<script async setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const visible = ref(false);

const lockBody = () => {
  document.body.style.overflow = "hidden";
};

const unlockBody = () => {
  document.body.style.overflow = "";
};

const open = () => {
  if (visible.value) return;
  visible.value = true;
  lockBody();
};

const close = () => {
  if (!visible.value) return;
  visible.value = false;
  unlockBody();
};

const onSearchOpenEvent = () => {
  open();
};

const onSearchCloseEvent = () => {
  close();
};

const onKeydown = (event) => {
  if (event.key === "Escape") {
    close();
  }
};

const searchTerm = ref("");
const searchResults = ref([]);
const isLoading = ref(false);

const allProducts = ref([]);
const visibleProducts = ref([]);
const hasVisibleProducts = ref(false);
const hasMoreProducts = ref(false);
const maxProducts = ref(6);

const lastRequestedTerm = ref("");

let mediaMd = null;
let mediaNotebook = null;

const updateMaxProducts = () => {
  if (mediaNotebook?.matches) {
    maxProducts.value = 6;
    return;
  }

  if (mediaMd?.matches) {
    maxProducts.value = 4;
    return;
  }

  maxProducts.value = 2;
};

const updateVisibleProducts = (list) => {
  const arr = Array.isArray(list) ? list : [];
  visibleProducts.value = arr.slice(0, maxProducts.value);
  hasVisibleProducts.value = visibleProducts.value.length > 0;
  hasMoreProducts.value = arr.length > maxProducts.value;
};

const searchDebounceTimer = ref(null);
let searchAbortController = null;

const fetchSearchResults = (term) => {
  const q = (term || "").trim();

  searchTerm.value = q;
  lastRequestedTerm.value = q;

  if (searchAbortController) {
    searchAbortController.abort();
    searchAbortController = null;
  }

  if (q.length < 2) {
    searchResults.value = [];
    allProducts.value = Array.isArray(props.config.popularProducts)
      ? props.config.popularProducts
      : [];
    updateVisibleProducts(allProducts.value);
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  searchAbortController = new AbortController();

  const lang =
    window?.languageSettings?.current_language ||
    document.documentElement.lang?.split("-")[0] ||
    "de";

  fetch(`/wp-json/kd/v1/search-products?s=${encodeURIComponent(q)}&lang=${encodeURIComponent(lang)}`, {
    signal: searchAbortController.signal,
  })
    .then((res) => (res.ok ? res.json() : []))
    .then((data) => {
      if (lastRequestedTerm.value !== q) {
        return;
      }

      const arr = Array.isArray(data) ? data : [];
      searchResults.value = arr;
      allProducts.value = arr;
      updateVisibleProducts(allProducts.value);
    })
    .catch((error) => {
      if (error?.name === "AbortError") {
        return;
      }

      if (lastRequestedTerm.value !== q) {
        return;
      }

      searchResults.value = [];
      allProducts.value = [];
      updateVisibleProducts(allProducts.value);
    })
    .finally(() => {
      if (lastRequestedTerm.value !== q) {
        return;
      }

      isLoading.value = false;
      searchAbortController = null;
    });
};

const onSearchQueryEvent = (event) => {
  const term =
    typeof event?.detail === "string"
      ? event.detail
      : String(event?.detail?.term || "");

  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }

  searchDebounceTimer.value = setTimeout(() => {
    fetchSearchResults(term);
  }, 300);
};

watch(maxProducts, () => {
  updateVisibleProducts(allProducts.value);
});

onMounted(() => {
  window.addEventListener("kd:search:open", onSearchOpenEvent);
  window.addEventListener("kd:search:close", onSearchCloseEvent);
  window.addEventListener("kd:search:query", onSearchQueryEvent);
  window.addEventListener("keydown", onKeydown);

  mediaMd = window.matchMedia("(min-width: 767px)");
  mediaNotebook = window.matchMedia("(min-width: 1100px)");

  mediaMd.addEventListener("change", updateMaxProducts);
  mediaNotebook.addEventListener("change", updateMaxProducts);

  updateMaxProducts();

  allProducts.value = Array.isArray(props.config.popularProducts)
    ? props.config.popularProducts
    : [];
  updateVisibleProducts(allProducts.value);

  if (visible.value) {
    lockBody();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("kd:search:open", onSearchOpenEvent);
  window.removeEventListener("kd:search:close", onSearchCloseEvent);
  window.removeEventListener("kd:search:query", onSearchQueryEvent);
  window.removeEventListener("keydown", onKeydown);

  mediaMd?.removeEventListener("change", updateMaxProducts);
  mediaNotebook?.removeEventListener("change", updateMaxProducts);
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
  
  if (searchAbortController) {
    searchAbortController.abort();
  }
  unlockBody();
});

// Titel dynamisch berechnen
const getDynamicProductsTitle = () => {
  if (searchTerm.value.length > 0) {
    const tpl = props.config.searchResultsTitle || 'Suchergebnisse für "%s"';
    return tpl.replace("%s", searchTerm.value);
  }

  return props.config.productsTitle;
};

// explizit navigieren
const goToAllResults = () => {
  if (!searchTerm.value.length) return;

  const params = new URLSearchParams();
  params.set("s", searchTerm.value);
  params.set("post_type", "product");

  const url = `/?${params.toString()}`;

  close();
  window.location.href = url;
};

const onPopularClick = (label) => {
  window.dispatchEvent(
    new CustomEvent("kd:search:set-term", {
      detail: label,
    })
  );
};

const closeFromOverlay = () => {
  close();
  window.dispatchEvent(new CustomEvent("kd:search:overlay-close"));
};
</script>

<template>
    <!-- OVERLAY -->
    <div v-if="visible" class="fixed inset-0 bg-black/80 z-[90]" @click="closeFromOverlay"></div>
    <Transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="-translate-y-12 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="-translate-y-4 opacity-0"
    >
        <div @click.stop v-if="visible" class="fixed left-0 w-full top-14 z-[120]">
            <div class="bg-ts-white border-t border-ts-black shadow-md">
                <div
                    class="max-w-ts-content mx-auto px-6 pt-4 pb-8 relative flex flex-col gap-4 overflow-y-auto max-h-[80vh]"
                >
                    <div class="flex flex-col gap-6">
                        <!-- Beliebte Suchbegriffe / Kategorien -->
                        <section
                            v-if="Array.isArray(config.popularCategories) && config.popularCategories.length"
                        >
                            <p class="mb-3">
                                {{ config.categoriesTitle }}
                            </p>
                      
                            <div class="flex flex-wrap gap-2">
                                <kd-button
                                    v-for="cat in (Array.isArray(config.popularCategories) ? config.popularCategories : [])"
                                    :key="cat.id ?? cat.name"
                                    variant="black"
                                    @click.prevent="onPopularClick(cat.name)"
                                >
                                    {{ cat.name }}
                                </kd-button>
                            </div>
                        </section>

                        <!-- Produkte: entweder Bestseller (Config) oder Suchergebnisse -->
                        <section v-if="hasVisibleProducts || searchTerm.length || isLoading">
                            <p class="mb-3">
                                {{ getDynamicProductsTitle() }}
                            </p>
                      
                            <!-- Nur Spinner, wenn geladen wird -->
                            <div v-if="isLoading" class="flex justify-center py-4">
                                <kd-icon-loading
                                    fill="black"
                                    size="60"
                                    class="inline-block animate-spin"
                                />
                            </div>
                      
                            <!-- Nur Produkte, wenn fertig geladen -->
                            <div v-else-if="hasVisibleProducts" class="grid grid-cols-1 phone:grid-cols-2 notebook:grid-cols-3 gap-6">
                                <kd-product
                                    v-for="prod in visibleProducts"
                                    :key="prod.id"
                                    :product-data="prod"
                                    variant="search"
                                    class="rounded-xl overflow-hidden"
                                >
                                </kd-product>
                            </div>
                      
                            <p v-else class="text-sm text-black/80">
                                {{ config.noResultsText }}
                            </p>
                      
                            <div
                                v-if="hasMoreProducts && searchTerm.length && !isLoading"
                                class="mt-6 flex justify-center"
                            >
                                <a
                                    href="#"
                                    class="underline underline-offset-2 transition-all duration-200 hover:-translate-y-0.5"
                                    @click.prevent="goToAllResults"
                                >
                                    {{ config.seeAllResultsText }}
                                </a>
                            </div>
                        </section>
                    </div>

                    <!-- Fallback -->
                    <div
                        v-if="!(Array.isArray(config.popularCategories) && config.popularCategories.length) && !hasVisibleProducts"
                    >
                        {{ config.noConfigText }}
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>
